import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from '../i18n'
import { api, type Bid } from '../lib/api'
import { ConfirmModal } from './ConfirmModal'
import { CreateBidModal, type NewBidFields } from './CreateBidModal'
import { PersonChip } from './PersonChip'
import { RowMenu } from './RowMenu'
import { publishTourState } from '../tours/useTourState'
import { HOME_BID_CREATED_STATE } from '../tours/probes'

/** A few accent gradients, picked deterministically per bid so each card has a stable colour. */
const ACCENTS = [
  'linear-gradient(135deg,#6366f1,#0ea5e9)',
  'linear-gradient(135deg,#0ea5e9,#22d3ee)',
  'linear-gradient(135deg,#8b5cf6,#ec4899)',
  'linear-gradient(135deg,#10b981,#22d3ee)',
  'linear-gradient(135deg,#f59e0b,#ef4444)',
  'linear-gradient(135deg,#14b8a6,#6366f1)',
]
const accentFor = (id: string): string => {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return ACCENTS[h % ACCENTS.length]!
}
const initials = (name: string): string =>
  name
    .replace(/[^\p{L}\p{N} ]/gu, ' ')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '—'

/** A client name longer than this gets the smaller type in the tile's top slot (and still
 *  ellipsizes past the slot's width) — `.bidtile-client.is-long` in styles.css. */
const LONG_CLIENT = 18

/** The bid's Bid Manager, as the tile shows them: the roster member holding that role, if any. */
const bidManagerOf = (b: Bid) => (b.members ?? []).find((m) => m.role === 'BidManager')

/** When the bid last changed, as a sortable number. A bid without a (valid) date sorts last. */
const modifiedAt = (b: Bid): number => Date.parse(b.lastModified ?? '') || 0

/**
 * The application home — the default landing. Shows the member's current bids as cards and
 * lets them create a new bid. Picking a card opens that bid's workspace (its overview).
 */
export function HomePage({
  bids,
  loading,
  onOpenBid,
}: {
  bids: Bid[]
  loading: boolean
  onOpenBid: (id: string) => void
}) {
  const { t } = useLang()
  const qc = useQueryClient()
  const [creating, setCreating] = useState(false)
  const [deletingBid, setDeletingBid] = useState<Bid | null>(null)
  const [query, setQuery] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  // The two role toggles beside the title — independent, like the chat's Bid KB / Corporate KB
  // pair. Both on (the default) lists every accessible bid, including a tenant admin's bids they
  // are not on the roster of; with only one on, a bid needs the caller's role to match it.
  const [asBm, setAsBm] = useState(true)
  const [asContributor, setAsContributor] = useState(true)
  // The caller's own role on each bid rides on GET /v1/me (the same cached query lib/bid-profile.ts
  // reads), so the role filter needs no extra round trip.
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 })
  // Only bid managers create bids (POST /v1/bids 403s anyone else), so "New bid" is theirs alone.
  // Unresolved reads as "may not": the control appears a moment late rather than flashing at
  // members who could never use it.
  const canCreate = me.data?.canCreateBids ?? false

  // Fulltext filter over bid meta-data: the bid's own name, its client, and the names of its
  // members so far — all already ride along on GET /v1/bids (services.ts#listMyBids), so this is
  // client-side only. Then the role toggles, then the most recently modified bid first.
  const filteredBids = useMemo(() => {
    const q = query.trim().toLowerCase()
    const roleOf = new Map((me.data?.memberships ?? []).map((m) => [m.bidId, m.role]))
    const matchesRole = (b: Bid): boolean => {
      if (asBm && asContributor) return true
      const role = roleOf.get(b.id)
      if (!role) return false
      return role === 'BidManager' ? asBm : asContributor
    }
    return bids
      .filter(
        (b) =>
          !q ||
          b.name.toLowerCase().includes(q) ||
          (b.client ?? '').toLowerCase().includes(q) ||
          (b.members ?? []).some((m) => m.displayName.toLowerCase().includes(q)),
      )
      .filter(matchesRole)
      .sort((a, b) => modifiedAt(b) - modifiedAt(a))
  }, [bids, query, me.data, asBm, asContributor])

  async function createBid(fields: NewBidFields): Promise<void> {
    const bid = await api.createBid(fields)
    // A guided tour's "create your own test bid" step (tours/probes.ts): when, never what.
    publishTourState(HOME_BID_CREATED_STATE, { at: Date.now() })
    setCreating(false)
    // ['me'] as well as ['bids']: creating a bid writes the creator onto its roster as `admin`
    // (services.ts#createBid), and that membership is what every per-bid affordance reads
    // (lib/bid-profile.ts). Without this the cached /v1/me still predates the bid, so the
    // overview we are about to open renders it as if the member had no profile in it — the
    // editor-only actions (setting the bid's SharePoint site) stay hidden until a reload.
    // Awaited before navigating so the overview mounts on a roster that already knows them.
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['bids'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ])
    onOpenBid(bid.id)
  }

  // Re-pulls GET /v1/bids (another member may have created/renamed/deleted one since this list
  // was fetched). Same query key the initial load and the create/delete handlers above already
  // invalidate — this just does it on demand instead of waiting for a mutation to trigger it.
  async function refreshBids(): Promise<void> {
    setRefreshing(true)
    try {
      await qc.invalidateQueries({ queryKey: ['bids'] })
    } finally {
      setRefreshing(false)
    }
  }

  async function deleteBid(bid: Bid): Promise<void> {
    await api.deleteBid(bid.id)
    setDeletingBid(null)
    // The roster went with it — same reason as above, in reverse.
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['bids'] }),
      qc.invalidateQueries({ queryKey: ['me'] }),
    ])
  }

  return (
    <main className="homepage" aria-label="Home">
      {/* "bare": the title leads the hero — no eyebrow above it, no subtitle below it. */}
      <div className="home-hero home-hero-bare">
        <div className="home-hero-in home-hero-row">
          <div className="home-title">
            <h1>{t('home_title')}</h1>
            {/* Same pill toggles (and markup) as the chat's Bid KB / Corporate KB pair: independently
                toggleable, not a radio group. */}
            <div className="home-roletoggle" role="group" aria-label={t('home_role_group')}>
              <div className="seg">
                <button type="button" className={asBm ? 'on' : ''} aria-pressed={asBm}
                  title={t('home_role_bm_title')} onClick={() => setAsBm((v) => !v)}>{t('home_role_bm')}</button>
                <button type="button" className={asContributor ? 'on' : ''} aria-pressed={asContributor}
                  title={t('home_role_contributor_title')} onClick={() => setAsContributor((v) => !v)}>{t('home_role_contributor')}</button>
              </div>
            </div>
          </div>
          <div className="home-search-wrap">
            <input
              type="search"
              className="home-search"
              placeholder={t('search_bids')}
              aria-label={t('search_bids')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            {query && (
              <button
                type="button"
                className="home-search-clear"
                aria-label={t('clear_search')}
                title={t('clear_search')}
                onClick={() => setQuery('')}
              >
                <span aria-hidden="true">×</span>
              </button>
            )}
          </div>
          <div className="home-actions">
            {canCreate && (
              <button className="home-cta" data-help-id="home-new-bid" onClick={() => setCreating(true)}>
                <span aria-hidden="true">＋</span> {t('new_bid')}
              </button>
            )}
            <button
              type="button"
              className="home-refresh"
              aria-label={t('refresh_bids')}
              title={t('refresh_bids')}
              disabled={refreshing}
              onClick={() => void refreshBids()}
            >
              <span aria-hidden="true" className={refreshing ? 'home-refresh-spin' : undefined}>⟳</span>
            </button>
          </div>
        </div>
      </div>

      <section className="home-grid" aria-busy={loading}>
        {loading && bids.length === 0 && (
          <div className="home-empty">{t('loading')}</div>
        )}
        {!loading && bids.length === 0 && (
          // A bid manager starts their first bid; anyone else joins one by being added to it.
          <div className="home-empty">
            {!me.data ? t('loading') : canCreate ? t('no_bids') : t('no_bids_member')}
          </div>
        )}
        {!loading && bids.length > 0 && filteredBids.length === 0 && (
          <div className="home-empty">{t('no_bid_matches')}</div>
        )}

        {filteredBids.map((b) => {
          const client = b.client?.trim()
          const manager = bidManagerOf(b)
          return (
            <div key={b.id} className="bidtile-wrap">
              <button className="bidtile" onClick={() => onOpenBid(b.id)}>
                {/* Top: the client, or — with none — the name's monogram, which is decoration only
                    (the name is read out below it), hence hidden from assistive tech. */}
                <span
                  className="bidtile-art"
                  style={{ background: accentFor(b.id) }}
                  aria-hidden={client ? undefined : true}
                >
                  {client ? (
                    <span
                      className={'bidtile-mark bidtile-client' + (client.length > LONG_CLIENT ? ' is-long' : '')}
                      title={client}
                    >
                      {client}
                    </span>
                  ) : (
                    <span className="bidtile-mark">{initials(b.name)}</span>
                  )}
                </span>
                {/* Bottom: the bid name (which is the opportunity's name), then the Bid Manager. */}
                <span className="bidtile-body">
                  <span className="bidtile-name">{b.name}</span>
                  <span className="bidtile-lead">
                    {manager ? <PersonChip email={manager.email} displayName={manager.displayName} /> : '—'}
                  </span>
                </span>
              </button>
              {/* A sibling of the card button, not inside it: its clicks never open the bid. */}
              <RowMenu
                className="bidtile-menu"
                label={t('bid_actions')}
                items={[{ label: t('delete'), danger: true, onSelect: () => setDeletingBid(b) }]}
              />
            </div>
          )
        })}

        {canCreate && (
          <button className="bidtile bidtile-new" onClick={() => setCreating(true)}>
            <span className="bidtile-plus" aria-hidden="true">＋</span>
            <span className="bidtile-name">{t('new_bid')}</span>
          </button>
        )}
      </section>

      {creating && canCreate && (
        <CreateBidModal bids={bids} onSubmit={createBid} onClose={() => setCreating(false)} />
      )}

      {deletingBid && (
        <ConfirmModal
          title={t('delete_bid_title')}
          message={t('confirm_delete_bid')}
          danger
          onConfirm={() => void deleteBid(deletingBid)}
          onClose={() => setDeletingBid(null)}
        />
      )}
    </main>
  )
}
